import * as React from 'react';
import { createGlobalStyle } from 'styled-components';
import { GlobalStyles } from '@mui/material';
import { heartbeat, slideDown, fadeIn, shake, flamePulse, successCheck } from './animations';
import { duration, easing } from './theme';

/**
 * Styled Components global style reset and base styles.
 * Everything references theme tokens - no hardcoded values.
 */
export const GlobalStyle = createGlobalStyle`
  /* ── Box-sizing reset ─────────────────────────────────────────────── */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* ── HTML & Body ──────────────────────────────────────────────────── */
  html {
    scroll-behavior: smooth;
    -webkit-text-size-adjust: 100%;
    text-size-adjust: 100%;
  }

  body {
    min-height: 100vh;
    text-rendering: optimizeLegibility;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    line-height: 1.5;
    font-family: ${({ theme }) => theme.fonts.body};
    background-color: ${({ theme }) => theme.colors.background};
    color: ${({ theme }) => theme.colors.text};
    transition:
      background-color ${duration.normal} ${easing.easeInOut},
      color ${duration.normal} ${easing.easeInOut};
  }

  #root {
    isolation: isolate;
  }

  /* ── Typography resets ────────────────────────────────────────────── */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${({ theme }) => theme.fonts.heading};
    overflow-wrap: break-word;
  }

  p, li, dd, blockquote, figcaption {
    overflow-wrap: break-word;
  }

  /* ── Remove default list styles ───────────────────────────────────── */
  ul[class], ol[class] {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  /* ── Links ────────────────────────────────────────────────────────── */
  a:not([class]) {
    text-decoration-skip-ink: auto;
    color: ${({ theme }) => theme.colors.primary};
  }

  /* ── Media ────────────────────────────────────────────────────────── */
  img, picture, video, canvas, svg {
    display: block;
    max-width: 100%;
  }

  img {
    image-rendering: auto;
  }

  /* ── Form elements ────────────────────────────────────────────────── */
  input, button, textarea, select {
    font: inherit;
  }

  /* ── Selection color ──────────────────────────────────────────────── */
  ::selection {
    background-color: ${({ theme }) => theme.colors.primaryLight};
    color: ${({ theme }) => theme.colors.text};
  }

  /* ── Focus-visible ring ───────────────────────────────────────────── */
  *:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 2px;
  }

  /* ── Skip link ────────────────────────────────────────────────────── */
  .skip-link {
    position: absolute;
    top: -${({ theme }) => theme.spacing[10]};
    left: 0;
    background: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.textOnPrimary};
    padding: ${({ theme }) => theme.spacing[2]} ${({ theme }) => theme.spacing[4]};
    z-index: 10000;
    font-weight: ${({ theme }) => theme.fontWeights.bold};
    text-decoration: none;
    border-radius: 0 0 ${({ theme }) => theme.radii.md} 0;
    transition: top ${duration.fast} ${easing.easeOut};
  }

  .skip-link:focus {
    top: 0;
  }

  /* ── Like animation (used via className) ──────────────────────────── */
  .like-animate {
    @media (prefers-reduced-motion: no-preference) {
      animation: ${heartbeat} 500ms ${easing.spring};
    }
  }

  /* ── Page fade-in transition ──────────────────────────────────────── */
  .page-fade-enter {
    @media (prefers-reduced-motion: no-preference) {
      animation: ${fadeIn} ${duration.fast} ${easing.easeOut};
    }
  }

  /* ── Comment section expand ───────────────────────────────────────── */
  .comment-expand {
    overflow: hidden;
    @media (prefers-reduced-motion: no-preference) {
      animation: ${slideDown} ${duration.normal} ${easing.easeOut};
    }
  }

  /* ── Error shake animation (input fields) ──────────────────────── */
  .error-shake {
    @media (prefers-reduced-motion: no-preference) {
      animation: ${shake} ${duration.slow} ${easing.easeOut};
    }
  }

  /* ── Streak flame pulse animation ──────────────────────────────── */
  .flame-pulse {
    @media (prefers-reduced-motion: no-preference) {
      animation: ${flamePulse} 2s ${easing.easeInOut} infinite;
    }
  }

  /* ── Success checkmark animation ───────────────────────────────── */
  .success-check {
    @media (prefers-reduced-motion: no-preference) {
      animation: ${successCheck} ${duration.normal} ${easing.easeOut};
    }
  }

  /* ── Follow button transition ──────────────────────────────────── */
  .follow-transition {
    @media (prefers-reduced-motion: no-preference) {
      transition: all ${duration.fast} ${easing.easeOut};
    }
  }

  /* ── prefers-reduced-motion: kill all animations ──────────────────── */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* ── Custom scrollbar (WebKit) ────────────────────────────────────── */
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  ::-webkit-scrollbar-track {
    background: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colors.border};
    border-radius: ${({ theme }) => theme.radii.full};
    border: 2px solid transparent;
    background-clip: padding-box;
  }

  ::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colors.textMuted};
  }

  /* ── Custom scrollbar (Firefox) ───────────────────────────────────── */
  * {
    scrollbar-width: thin;
    scrollbar-color: ${({ theme }) => theme.colors.border} transparent;
  }
`;

/**
 * MUI global style overrides (autofill fix, etc.).
 */
export const MuiGlobalStyles = () => (
  <GlobalStyles styles={{
    'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, input:-webkit-autofill:active': {
      transitionDelay: '9999s',
      transitionProperty: 'background-color, color',
      backgroundColor: 'background.default !important',
      color: 'primary.main !important',
    },
    '&::-ms-reveal, &::-webkit-credentials-auto-fill-button': {
      display: 'none !important',
    },
  }} />
);
